import { useEffect } from 'react';
import { FormInstance } from 'antd';

import { FileForCustomReport } from '@/components/ui/Inputs/types/response/file';
import { BERA_REPORT_VERSION } from '../constants';
import { BeraJobSummaryDTO, BeraReport } from './types/response';
import { getUnfinishedFileNavigation } from './useUnfinishedFileNavigation';

const REPORT_STEP = 2;
const FILE_STEP = 1;
const FIRST_STEP = 0;

type SummaryFileEntry = BeraJobSummaryDTO['bera_job_summary_files'][number];

function createEmptyCustomReportsFiles(customReportId: string, fileId: string) {
	return {
		id: '',
		created_at: null as any,
		updated_at: null as any,
		deleted_at: null as any,
		custom_report_id: customReportId,
		file_id: fileId
	};
}

type SummaryFile = { id: string; original_name: string };

function buildFileFromSummaryFile(
	file: SummaryFile,
	jobSummary: BeraJobSummaryDTO,
	customReportId: string
): FileForCustomReport {
	return {
		id: file.id,
		original_name: file.original_name,
		generated_name: '',
		created_at: jobSummary.updated_at,
		updated_at: jobSummary.updated_at,
		task: [],
		CustomReportsFiles: createEmptyCustomReportsFiles(customReportId, file.id)
	} as FileForCustomReport;
}

function buildFileFromSummaryEntry(
	entry: SummaryFileEntry,
	jobSummary: BeraJobSummaryDTO,
	customReportId: string
): FileForCustomReport {
	return buildFileFromSummaryFile(entry.file, jobSummary, customReportId);
}

function enrichFileWithReport(
	existing: FileForCustomReport | undefined,
	report: BeraReport,
	customReportId: string
): FileForCustomReport {
	const fileId = report.file_id;
	return {
		...(existing || {
			generated_name: '',
			task: [],
			CustomReportsFiles: createEmptyCustomReportsFiles(customReportId, fileId)
		}),
		id: fileId,
		original_name: report.file_original_name,
		created_at: report.updated_at,
		updated_at: report.updated_at,
		bera_report: [
			{
				id: report.id,
				rpn: report.rpn,
				updated_at: report.updated_at,
				bera_job_summary_id: report.bera_job_summary_id
			}
		]
	} as FileForCustomReport;
}

function buildFilesFromJobSummary(jobSummary: BeraJobSummaryDTO, customReportId: string): FileForCustomReport[] {
	const fileMap = new Map<string, FileForCustomReport>();

	if (jobSummary.file) {
		fileMap.set(jobSummary.file.id, buildFileFromSummaryFile(jobSummary.file, jobSummary, customReportId));
	}

	(jobSummary.bera_job_summary_files || []).forEach((entry) => {
		if (!fileMap.has(entry.file.id)) {
			fileMap.set(entry.file.id, buildFileFromSummaryEntry(entry, jobSummary, customReportId));
		}
	});

	(jobSummary.bera_report || []).forEach((report) => {
		fileMap.set(report.file_id, enrichFileWithReport(fileMap.get(report.file_id), report, customReportId));
	});

	return Array.from(fileMap.values());
}

function mergeFiles(existing: FileForCustomReport[], incoming: FileForCustomReport[]): FileForCustomReport[] {
	const mergedMap = new Map<string, FileForCustomReport>();
	existing.forEach((f) => mergedMap.set(String(f.id), f));
	incoming.forEach((f) => {
		if (!mergedMap.has(String(f.id))) {
			mergedMap.set(String(f.id), f);
		}
	});
	return Array.from(mergedMap.values());
}

interface UseJobSummaryFilesParams {
	jobSummary: BeraJobSummaryDTO | undefined;
	customReportId: string | undefined;
	selectedFiles: FileForCustomReport[];
	selectedFilesId: string[] | React.Key[];
	form: FormInstance;
	step: number;
	handleSelectedFilesChange: (files: FileForCustomReport[]) => void;
	handleShowBeraJobResultChange: (value: boolean) => void;
	handleCurrentFileIdChange: (id: string) => void;
	setStep: (step: number) => void;
}

export function useJobSummaryFiles({
	jobSummary,
	customReportId,
	selectedFiles,
	selectedFilesId,
	form,
	step,
	handleSelectedFilesChange,
	handleShowBeraJobResultChange,
	handleCurrentFileIdChange,
	setStep
}: UseJobSummaryFilesParams) {
	function handleBuildFilesFromJobSummary() {
		const { hasUnfinishedFiles, firstUnfinishedFileId } = getUnfinishedFileNavigation(jobSummary);

		if (hasUnfinishedFiles && firstUnfinishedFileId) {
			handleCurrentFileIdChange(firstUnfinishedFileId);
			return;
		}

		handleShowBeraJobResultChange(true);
	}
	useEffect(() => {
		if (!jobSummary?.id || !customReportId) return;

		const isRevisionWithoutFile = !!jobSummary.is_revision && !jobSummary.file_id;

		if (isRevisionWithoutFile && step === FIRST_STEP) {
			setStep(FILE_STEP);
		}

		if (isRevisionWithoutFile) {
			return;
		}

		const jobSummaryFiles = buildFilesFromJobSummary(jobSummary, customReportId);

		if (jobSummaryFiles.length > 0) {
			const currentSet = new Set(selectedFilesId.map(String));
			const hasMissing = jobSummaryFiles.some((f) => !currentSet.has(String(f.id)));

			if (hasMissing) {
				const merged = mergeFiles(selectedFiles, jobSummaryFiles);
				form.setFieldValue(
					'files_list',
					merged.map((f) => f.id)
				);
				handleSelectedFilesChange(merged);
			}
		}

		const isVersionTwo = jobSummary?.version === BERA_REPORT_VERSION.V2;

		if (jobSummary?.show_bera_result && step === FIRST_STEP && !isVersionTwo) {
			handleBuildFilesFromJobSummary();
			setStep(REPORT_STEP);

			return;
		}

		const isVersionTwoInProgress = isVersionTwo && jobSummaryFiles.length > 0;

		if (isVersionTwoInProgress && step === FIRST_STEP) {
			setStep(REPORT_STEP);
		}
	}, [jobSummary, customReportId]);
}
